/**
 * Free grid design (research/taxonomy.md §1.5 spine / tree, and the irregular layouts no other
 * family describes: pilot-v1 p14, p16, R2-07): a front elevation of straight boards at right
 * angles, each placed on its own. The parameter block lives in `design.params`.
 *
 * Geometry is stored as proportions, so W, H, the base and the stock can change without
 * refitting anything; the generator snaps to whole millimetres and resolves the junctions.
 *
 * - `uprights[i] = { x, y0, y1 }`: `x` places the board's LEFT face at x · (W − t), so 0 is flush
 *   with the left edge and 1 flush with the right; `y0`, `y1` are its bottom and top as
 *   fractions of the carcass height (H less the feet): 0 the floor, 1 the top.
 * - `shelves[k] = { y, x0, x1 }`: `y` places the board's UNDERSIDE at y · (Hc − t) above the
 *   floor (or the feet), so 0 is a bottom board on the floor and 1 a top board flush with the
 *   top; `x0`, `x1` are its outer ends as fractions of W.
 * - `backs[b] = { x0, x1, y0, y1 }` (optional): back boards in the back plane, behind some
 *   compartments or rising above a shelf (pilot-v1-r3 p16), as fractions of W and of the carcass
 *   height like an upright's. With any back board, the shelves and uprights stand one board
 *   thickness forward and the back boards are screwed onto their back edges.
 *
 * Junction rules (generate.ts): a board that ends at another butts into it; where a shelf and an
 * upright cross, the shelf runs through and the upright is cut in two; at a corner where both
 * end, the upright runs through and the shelf butts into it. A shelf held by one upright only
 * (a spine or E-shape) is a rated cantilever: deep-housed with hidden steel brackets, and the
 * piece must be fixed to the wall.
 */
import type { Appearance, Design } from '../../domain/schemas';
import { mm } from '../../domain/units';
import type { BaseParams } from '../base';

export const FREE_ID = 'free-grid';
export const FREE_VERSION = '1.0';
export const FREE_LABEL = 'Free layout';
export const FREE_STOCK = 'birch-ply-18';
export const MAX_SHELVES = 16;
export const MAX_UPRIGHTS = 12;

export interface Upright { x: number; y0: number; y1: number }
export interface Shelf { y: number; x0: number; x1: number }
export interface BackBoard { x0: number; x1: number; y0: number; y1: number }
export const MAX_BACKS = 8;

export interface FreeParams extends BaseParams {
  uprights: Upright[];
  shelves: Shelf[];
  /** Back boards in the back plane; absent or [] = an open back (the housings and the wall fixing take the racking). */
  backs?: BackBoard[];
  /** Kept for older designs; the back is described by `backs`. */
  back: 'none';
  /** Parts the fit left out or added to make the fitted layout buildable (for the fidelity check). */
  buildableChanges?: string[];
}

export const DEFAULT_APPEARANCE: Readonly<Appearance> = Object.freeze({ name: 'Free layout, my version', staging: 'studio', props: true });


/** A closed box with the given inner shelves and uprights (fractions as in the module comment). */
function box(inner: { shelves?: Shelf[]; uprights?: Upright[] } = {}): Pick<FreeParams, 'uprights' | 'shelves'> {
  return {
    uprights: [{ x: 0, y0: 0, y1: 1 }, { x: 1, y0: 0, y1: 1 }, ...(inner.uprights ?? [])],
    shelves: [{ y: 0, x0: 0, x1: 1 }, ...(inner.shelves ?? []), { y: 1, x0: 0, x1: 1 }],
  };
}

export type FreePresetKey = 'e-shape' | 'spine' | 'cubbies';

export const FREE_PRESETS: Readonly<Record<FreePresetKey, { label: string; description: string; W: number; H: number; D: number; stock: string; params: Pick<FreeParams, 'uprights' | 'shelves'> }>> = Object.freeze({
  'e-shape': {
    label: 'E-shape 450 × 600', description: '450 × 600 × 300, three shelves off one upright, sheesham',
    W: mm(450), H: mm(600), D: mm(300), stock: 'sheesham-board-38',
    params: { uprights: [{ x: 0, y0: 0, y1: 1 }], shelves: [{ y: 0, x0: 0, x1: 1 }, { y: 0.5, x0: 0, x1: 1 }, { y: 1, x0: 0, x1: 1 }] },
  },
  spine: {
    label: 'Spine 700 × 1600', description: '700 × 1600 × 250, shelves either side of a central spine',
    W: mm(700), H: mm(1600), D: mm(250), stock: 'oak-board-20',
    params: {
      uprights: [{ x: 0.5, y0: 0, y1: 1 }],
      shelves: [{ y: 0, x0: 0.2, x1: 0.8 }, { y: 0.25, x0: 0.15, x1: 0.5 }, { y: 0.45, x0: 0.5, x1: 0.85 }, { y: 0.65, x0: 0.15, x1: 0.5 }, { y: 0.85, x0: 0.5, x1: 0.85 }],
    },
  },
  cubbies: {
    label: 'Irregular cubbies 720 × 900', description: '720 × 900 × 260, mixed compartments',
    W: mm(720), H: mm(900), D: mm(260), stock: 'birch-ply-18',
    params: box({
      shelves: [{ y: 0.3, x0: 0, x1: 0.62 }, { y: 0.55, x0: 0.38, x1: 1 }, { y: 0.78, x0: 0, x1: 0.62 }],
      uprights: [{ x: 0.38, y0: 0, y1: 0.55 }, { x: 0.62, y0: 0.3, y1: 1 }],
    }),
  },
});

export function defaultFreeParams(): FreeParams {
  return { ...JSON.parse(JSON.stringify(FREE_PRESETS.cubbies.params)) as Pick<FreeParams, 'uprights' | 'shelves'>, back: 'none', base: 'none' };
}

export function freeParamsOf(d: Pick<Design, 'params'>): FreeParams {
  const p = (d.params ?? {}) as Partial<FreeParams>;
  const def = defaultFreeParams();
  return { ...def, ...p, uprights: Array.isArray(p.uprights) ? p.uprights : def.uprights, shelves: Array.isArray(p.shelves) ? p.shelves : def.shelves, back: 'none' };
}

export function createFree(key: FreePresetKey = 'cubbies'): Design {
  const p = FREE_PRESETS[key] ?? FREE_PRESETS.cubbies;
  return {
    family: FREE_ID, W: p.W, H: p.H, D: p.D, n: 0, even: true, openings: [],
    stockProfileId: p.stock, constructionProfileId: 'free-grid-joints', appearance: { ...DEFAULT_APPEARANCE },
    params: { ...defaultFreeParams(), ...JSON.parse(JSON.stringify(p.params)) } as unknown as Record<string, unknown>,
  };
}

/** Edited fractions keep five decimals, so a whole-millimetre entry survives the round trip. */
const clamp01 = (v: unknown) => Math.round(Math.max(0, Math.min(1, Number(v) || 0)) * 1e5) / 1e5;
const tidyUpright = (u: Upright): Upright => { const a = clamp01(u.y0), b = clamp01(u.y1); return { x: clamp01(u.x), y0: Math.min(a, b), y1: Math.max(a, b) }; };
const tidyShelf = (s: Shelf): Shelf => { const a = clamp01(s.x0), b = clamp01(s.x1); return { y: clamp01(s.y), x0: Math.min(a, b), x1: Math.max(a, b) }; };
const tidyBack = (k: BackBoard): BackBoard => {
  const a = clamp01(k.x0), b = clamp01(k.x1), c = clamp01(k.y0), e = clamp01(k.y1);
  return { x0: Math.min(a, b), x1: Math.max(a, b), y0: Math.min(c, e), y1: Math.max(c, e) };
};

const withParams = (d: Design, p: FreeParams): Design => ({ ...d, appearance: { ...d.appearance }, params: JSON.parse(JSON.stringify(p)) as Record<string, unknown> });

/** Set `uprights`, `shelves` (tidied: fractions clamped to 0–1, ends in order) or any other key. */
export function setFreeParam(d: Design, key: string, value: unknown): Design {
  const p = freeParamsOf(d);
  if (key === 'uprights' && Array.isArray(value)) return withParams(d, { ...p, uprights: (value as Upright[]).slice(0, MAX_UPRIGHTS).map(tidyUpright) });
  if (key === 'shelves' && Array.isArray(value)) return withParams(d, { ...p, shelves: (value as Shelf[]).slice(0, MAX_SHELVES).map(tidyShelf) });
  if (key === 'backs' && Array.isArray(value)) return withParams(d, { ...p, backs: (value as BackBoard[]).slice(0, MAX_BACKS).map(tidyBack) });
  if (key === 'back') return withParams(d, p);
  return withParams(d, { ...p, [key]: value } as FreeParams);
}
